import React, { useEffect, useRef, useState } from 'react';
import {
  CONDICIONES, TIPOS_VEHICULO, TIPOS_MOTOR,
  consultaModelos, consultaMarcas, consultaTipos, consultaDescripciones, getStaticData,
} from './catalogoAutos';

/* Picker de vehículo reusando el catálogo del cotizador (API rodac), AISLADO.
   Cascada: Condición → Tipo vehículo → Modelo(año) → Marca → Tipo → Versión.
   Emite onChange(vehicle) con los valores seleccionados. */

// Etiqueta defensiva para listas de getStaticData (no conocemos el shape exacto).
const lbl = (o) => (o == null ? '' : (o.state ?? o.name ?? o.nombre ?? o.status ?? o.label ?? o.regime ?? o.descripcion ?? String(o)));
const val = (o) => (o == null ? '' : (o.id ?? o.value ?? lbl(o)));

export default function VehiculoCatalogPicker({ onChange }) {
  const [condicionId, setCondicionId] = useState('');
  const [tipoVehId, setTipoVehId] = useState('');

  const [modelos, setModelos] = useState([]);
  const [modelId, setModelId] = useState('');

  const [marcas, setMarcas] = useState([]);
  const [marca, setMarca] = useState(''); // brand (Valor)

  const [tipos, setTipos] = useState([]);
  const [tipoSel, setTipoSel] = useState(''); // type (Valor)

  const [versiones, setVersiones] = useState([]);
  const [version, setVersion] = useState('');

  const [motor, setMotor] = useState('');
  const [precio, setPrecio] = useState('');
  const [accesorios, setAccesorios] = useState('');

  const [regimes, setRegimes] = useState([]);
  const [regimen, setRegimen] = useState('');
  const [estados, setEstados] = useState([]);
  const [estadoPlaca, setEstadoPlaca] = useState('');

  const [loading, setLoading] = useState({});
  const setL = (k, v) => setLoading((s) => ({ ...s, [k]: v }));

  // Catálogos locales (régimen / estado de placa) al montar.
  useEffect(() => {
    getStaticData()
      .then((d) => { setRegimes(d.regimes || []); setEstados(d.state || []); })
      .catch(() => {});
  }, []);

  // Condición → Modelos(año). Resetea lo de abajo.
  useEffect(() => {
    setModelos([]); setModelId(''); setMarcas([]); setMarca('');
    setTipos([]); setTipoSel(''); setVersiones([]); setVersion('');
    if (!condicionId) return;
    setL('modelos', true);
    consultaModelos(condicionId).then(setModelos).catch(() => setModelos([])).finally(() => setL('modelos', false));
  }, [condicionId]);

  // (Tipo vehículo + Modelo) → Marcas.
  useEffect(() => {
    setMarcas([]); setMarca(''); setTipos([]); setTipoSel(''); setVersiones([]); setVersion('');
    if (!tipoVehId || !modelId) return;
    setL('marcas', true);
    consultaMarcas(tipoVehId, modelId).then(setMarcas).catch(() => setMarcas([])).finally(() => setL('marcas', false));
  }, [tipoVehId, modelId]);

  // Marca → Tipos.
  useEffect(() => {
    setTipos([]); setTipoSel(''); setVersiones([]); setVersion('');
    if (!tipoVehId || !modelId || !marca) return;
    setL('tipos', true);
    consultaTipos(tipoVehId, modelId, marca).then(setTipos).catch(() => setTipos([])).finally(() => setL('tipos', false));
  }, [marca]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tipo → Versiones (descripciones).
  useEffect(() => {
    setVersiones([]); setVersion('');
    if (!tipoVehId || !modelId || !marca || !tipoSel) return;
    setL('versiones', true);
    consultaDescripciones(tipoVehId, modelId, marca, tipoSel).then(setVersiones).catch(() => setVersiones([])).finally(() => setL('versiones', false));
  }, [tipoSel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Emite el vehículo seleccionado al padre.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    const condicion = CONDICIONES.find((c) => String(c.id) === String(condicionId))?.status || null;
    const tipoVeh = TIPOS_VEHICULO.find((t) => String(t.id) === String(tipoVehId))?.type || null;
    const anio = modelos.find((m) => String(m.id) === String(modelId))?.model || null;
    onChangeRef.current?.({
      condicion,
      tipo: tipoVeh,
      year: anio ? Number(String(anio).replace(/\D/g, '')) || null : null,
      brand: marca || null,
      trim_type: tipoSel || null,
      version: version || null,
      motor: motor || null,
      regimen: regimen || null,
      estado_placa: estadoPlaca || null,
      precio: precio !== '' ? Number(precio) : null,
      accesorios: accesorios !== '' ? Number(accesorios) : null,
    });
  }, [condicionId, tipoVehId, modelId, modelos, marca, tipoSel, version, motor, regimen, estadoPlaca, precio, accesorios]);

  return (
    <>
      <div className="field"><label>Condición</label>
        <select value={condicionId} onChange={(e) => setCondicionId(e.target.value)}>
          <option value="">Selecciona…</option>
          {CONDICIONES.map((c) => <option key={c.id} value={c.id}>{c.status}</option>)}
        </select>
      </div>

      <div className="field"><label>Tipo de vehículo</label>
        <select value={tipoVehId} onChange={(e) => setTipoVehId(e.target.value)}>
          <option value="">Selecciona…</option>
          {TIPOS_VEHICULO.map((t) => <option key={t.id} value={t.id}>{t.type}</option>)}
        </select>
      </div>

      <div className="field"><label>Año (modelo){loading.modelos ? ' · cargando…' : ''}</label>
        <select value={modelId} onChange={(e) => setModelId(e.target.value)} disabled={!modelos.length}>
          <option value="">{condicionId ? 'Selecciona…' : 'Elige condición primero'}</option>
          {modelos.map((m) => <option key={m.id} value={m.id}>{m.model}</option>)}
        </select>
      </div>

      <div className="field"><label>Marca{loading.marcas ? ' · cargando…' : ''}</label>
        <select value={marca} onChange={(e) => setMarca(e.target.value)} disabled={!marcas.length}>
          <option value="">{tipoVehId && modelId ? 'Selecciona…' : 'Elige tipo y año'}</option>
          {marcas.map((m) => <option key={m.id} value={m.brand}>{m.brand}</option>)}
        </select>
      </div>

      <div className="field"><label>Tipo{loading.tipos ? ' · cargando…' : ''}</label>
        <select value={tipoSel} onChange={(e) => setTipoSel(e.target.value)} disabled={!tipos.length}>
          <option value="">{marca ? 'Selecciona…' : 'Elige marca'}</option>
          {tipos.map((t) => <option key={t.id} value={t.type}>{t.type}</option>)}
        </select>
      </div>

      <div className="field"><label>Versión{loading.versiones ? ' · cargando…' : ''}</label>
        <select value={version} onChange={(e) => setVersion(e.target.value)} disabled={!versiones.length}>
          <option value="">{tipoSel ? 'Selecciona…' : 'Elige tipo'}</option>
          {versiones.map((v) => <option key={v.id} value={v.description}>{v.description}</option>)}
        </select>
      </div>

      <div className="field"><label>Tipo de motor</label>
        <select value={motor} onChange={(e) => setMotor(e.target.value)}>
          <option value="">Selecciona…</option>
          {TIPOS_MOTOR.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div className="field"><label>Régimen</label>
        <select value={regimen} onChange={(e) => setRegimen(e.target.value)}>
          <option value="">Selecciona…</option>
          {regimes.map((r, i) => <option key={val(r) || i} value={lbl(r)}>{lbl(r)}</option>)}
        </select>
      </div>

      <div className="field"><label>Estado de placa</label>
        <select value={estadoPlaca} onChange={(e) => setEstadoPlaca(e.target.value)}>
          <option value="">Selecciona…</option>
          {estados.map((s, i) => <option key={val(s) || i} value={lbl(s)}>{lbl(s)}</option>)}
        </select>
      </div>

      <div className="field"><label>Precio del vehículo (c/IVA)</label>
        <input type="number" step="0.01" placeholder="0.00" value={precio} onChange={(e) => setPrecio(e.target.value)} />
      </div>

      <div className="field"><label>Accesorios (c/IVA)</label>
        <input type="number" step="0.01" placeholder="0.00" value={accesorios} onChange={(e) => setAccesorios(e.target.value)} />
      </div>
    </>
  );
}
